// src/components/Footer.jsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Divider,
  Link,
} from "@mui/material";
import { motion } from "framer-motion";

// --- AI Kernel Imports ---
import { useKernel } from "./Shortener/hooks/AIKernel";

// --- Framer Motion Components ---
// Wrapping MUI Link to accept framer-motion props safely without console errors
const MotionLink = motion(Link);

const Footer = () => {
  // --- AI Engine Integration ---
  const { agentState, reportBehavior } = useKernel();
  const { t, i18n } = useTranslation();

  // 🧠 AI Actuator: Simplify UI if cognitive load is high
  const shouldSimplify = agentState.ui.simplifyAnimations;

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    // 🧠 AI Sensor: Track localization preferences
    reportBehavior(`language_changed_${lng}`, 3, 'behavior');
  };

  // Framer Motion Variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };

  return (
    <motion.footer
      initial={shouldSimplify ? "visible" : "hidden"}
      animate="visible"
      className={agentState.theme} // 🧠 Apply Dynamic AI Theme
      style={{
        background: "#0c0f14",
        borderTop: "1px solid rgba(255,255,255,0.07)",
        color: "#ffffff",
        paddingTop: 48,
        paddingBottom: 48,
      }}
    >
      <Container maxWidth="lg">
        {/* Language Switcher */}
        <motion.div variants={fadeInUp}>
          <Box display="flex" justifyContent="flex-end" mb={4}>
            <FormControl sx={{ minWidth: 140 }} size="small">
              <Select
                value={i18n.language}
                onChange={(e) => changeLanguage(e.target.value)}
                sx={{
                  bgcolor: "grey.800",
                  color: "common.white",
                  ".MuiSvgIcon-root": { color: "common.white" },
                }}
              >
                <MenuItem value="en">🇬🇧 English</MenuItem>
                <MenuItem value="si">🇱🇰 Sinhala</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </motion.div>

        <Grid container spacing={4}>
          {/* Company Info */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Linkpin
              </Typography>
              <Typography variant="body2" color="grey.400">
                Thoughtful short links, downloadable QR codes, and helpful local guidance.
              </Typography>
            </motion.div>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.quickLinks")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {[
                  { key: "home", label: t("home1"), path: "/" },
                  { key: "about", label: t("about1"), path: "/about" },
                  { key: "features", label: t("features1"), path: "/features" },
                  { key: "contact", label: t("contact1"), path: "/contact" },
                  { key: "dashboard", label: t("dashboard1"), path: "/dashboard" },
                ].map((link) => (
                  <MotionLink
                    component={RouterLink}
                    to={link.path}
                    color="grey.400"
                    underline="hover"
                    key={link.key}
                    // 🧠 AI Sensor: Track exactly what infrastructure component they care about
                    onClick={() => reportBehavior(`intent_nav_${link.key}`, 2, 'interest')}
                    whileHover={!shouldSimplify ? { color: "#8be0c5", x: 5 } : {}}
                    transition={{ type: "tween", duration: 0.2 }}
                  >
                    {link.label}
                  </MotionLink>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Resources */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.resources")}
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {[
                  { key: "features", label: t("features1"), path: "/features" },
                  { key: "about", label: t("about1"), path: "/about" },
                  { key: "contact", label: t("contact1"), path: "/contact" },
                  { key: "dashboard", label: t("dashboard1"), path: "/dashboard" },
                ].map((res) => (
                  <MotionLink
                    component={RouterLink}
                    to={res.path}
                    color="grey.400"
                    underline="hover"
                    key={res.key}
                    // 🧠 AI Sensor: Track technical/support intent
                    onClick={() => reportBehavior(`intent_resource_${res.key}`, 3, 'interest')}
                    whileHover={!shouldSimplify ? { color: "#8be0c5", x: 5 } : {}}
                    transition={{ type: "tween", duration: 0.2 }}
                  >
                    {res.label}
                  </MotionLink>
                ))}
              </Box>
            </motion.div>
          </Grid>

          {/* Contact & Social */}
          <Grid item xs={12} md={3}>
            <motion.div variants={fadeInUp}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                {t("footer.connect")}
              </Typography>
              <Box display="flex" gap={2} mb={2}>
                {/* Add SVG social icons with hover animations */}
              </Box>
              <Typography variant="body2" color="grey.400">
                Email:{" "}
                <a 
                    href="mailto:unicornprofessionalbay@gmail.com" 
                    style={{ color: "inherit", textDecoration: "none" }}
                    onClick={() => reportBehavior('direct_email_footer', 5, 'interest')}
                >
                    unicornprofessionalbay@gmail.com
                </a>
              </Typography>
              <Typography variant="body2" color="grey.400">
                Phone:{" "}
                <a 
                    href="tel:+94750788368" 
                    style={{ color: "inherit", textDecoration: "none" }}
                    onClick={() => reportBehavior('direct_phone_footer', 5, 'interest')}
                >
                    +94-75-0788368
                </a>
              </Typography>
            </motion.div>
          </Grid>
        </Grid>

        {/* Bottom Bar */}
        <Divider sx={{ bgcolor: "grey.700", my: 4 }} />
        <Box
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body2" color="grey.400">
            © {new Date().getFullYear()} Linkpin. {t("footer.rights")}
          </Typography>
          <Typography variant="body2" color="grey.500" mt={{ xs: 2, md: 0 }}>
            Link history is saved in this browser.
          </Typography>
        </Box>
      </Container>
    </motion.footer>
  );
};

export default Footer;